import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Info, Calendar, Thermometer, MessageCircle, Moon, Heart, CircleDot } from 'lucide-react';
import { getCyclePhase } from '@/utils/lunarPhases';

interface DynamicPhaseIndicatorProps {
  currentCycleDay: number;
  cycleLength: number;
  periodLength?: number;
  isPerimenopausal?: boolean;
  isMenopausal?: boolean;
}

export default function DynamicPhaseIndicator({ 
  currentCycleDay, 
  cycleLength, 
  periodLength = 5,
  isPerimenopausal = false,
  isMenopausal = false 
}: DynamicPhaseIndicatorProps) {
  
  // For perimenopause, don't show rigid phase calculations
  if (isPerimenopausal) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Your Perimenopause Journey
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border-2 border-primary/50 rounded-lg bg-primary/5">
            <h3 className="text-lg font-semibold text-primary mb-2">
              Honouring Your Transition
            </h3>
            <p className="text-sm text-muted-foreground">
              You're currently {currentCycleDay} days since your last period. During perimenopause, 
              cycles can vary greatly (21-60+ days is normal). Focus on tracking symptoms and patterns 
              rather than expecting regular phases.
            </p>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium">What to Track During Perimenopause:</h4>
            
            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <Thermometer className="h-5 w-5 text-primary" />
                <span className="font-medium">Physical Symptoms</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Hot flashes, night sweats, sleep changes, energy fluctuations
              </p>
            </div>

            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle className="h-5 w-5 text-primary" />
                <span className="font-medium">Emotional Patterns</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Mood shifts, anxiety, mental clarity, emotional sensitivity
              </p>
            </div>

            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <Moon className="h-5 w-5 text-primary" />
                <span className="font-medium">Lunar Alignment</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Notice how symptoms align with moon phases for deeper wisdom
              </p>
            </div>
          </div>

          <div className="p-3 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong>Remember:</strong> Irregular cycles are a normal part of perimenopause. 
              Your body is transitioning, and every woman's journey is unique. Trust your body's wisdom.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const currentPhase = getCyclePhase(currentCycleDay, cycleLength, isPerimenopausal, isMenopausal, periodLength);
  
  // Calculate phase ranges dynamically
  const lutealPhaseLength = Math.round(cycleLength * 0.43);
  const ovulationStart = cycleLength - lutealPhaseLength;
  const ovulationEnd = ovulationStart + 1;
  
  const phases = [
    {
      name: 'Menstruation',
      icon: <Heart className="h-5 w-5 text-red-500" />,
      range: `Days 1-${periodLength}`,
      color: 'text-red-500',
      description: 'Period days - hormone levels lowest'
    },
    {
      name: 'Follicular',
      icon: <CircleDot className="h-5 w-5 text-cyan-400" />,
      range: `Days ${periodLength + 1}-${ovulationStart - 1}`,
      color: 'text-cyan-400',
      description: 'Preparing for ovulation - estrogen rising'
    },
    {
      name: 'Ovulation',
      icon: <Heart className="h-5 w-5 text-pink-500" />,
      range: `Days ${ovulationStart}-${ovulationEnd}`,
      color: 'text-pink-500',
      description: 'Most fertile window - estrogen peaks'
    },
    {
      name: 'Luteal',
      icon: <Moon className="h-5 w-5 text-purple-500" />,
      range: `Days ${ovulationEnd + 1}-${cycleLength}`,
      color: 'text-purple-500',
      description: 'Pre-menstrual - progesterone rises then falls'
    }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5" />
          Your Dynamic Cycle Phases
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Phase Highlight */}
        <div className="p-4 border-2 border-primary/50 rounded-lg bg-primary/5">
          <div className="flex items-center justify-between mb-2">
            <Badge variant="default" className="text-sm">
              Current Phase
            </Badge>
            {phases.find(p => p.name === currentPhase.name)?.icon}
          </div>
          <h3 className={`text-lg font-semibold ${currentPhase.color}`}>
            {currentPhase.name}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Day {currentCycleDay} of {cycleLength} • {currentPhase.description}
          </p>
        </div>

        {/* All Phases Overview - Horizontal Layout */}
        <div>
          <h4 className="font-medium flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4" />
            Your {cycleLength}-Day Cycle Breakdown
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {phases.map((phase, index) => {
              const isCurrentPhase = phase.name === currentPhase.name;
              
              return (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${
                    isCurrentPhase 
                      ? 'border-primary/50 bg-primary/10' 
                      : 'border-muted bg-muted/30'
                  }`}
                >
                  <div className="flex flex-col items-center text-center gap-2">
                    {phase.icon}
                    <span className={`font-medium text-sm ${phase.color}`}>
                      {phase.name}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {phase.range}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {phase.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Personalization Note */}
        <div className="p-3 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
          <p className="text-xs text-muted-foreground">
            <strong>Personalised for you:</strong> These phases are calculated based on your {cycleLength}-day cycle length and {periodLength}-day period. 
            As you track more cycles, we can refine these predictions to match your unique pattern.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}